<template>
    <section id="installation" aria-label="Installation Instructions">
        <section v-if="!isCustomer" aria-label="Step 1">
            <h3>{{texts.installation.step}} {{stepOffset}}: {{texts.installation.createAccount}}</h3>
            <p>{{texts.installation.unlessYouHaveOne}}</p>
            <div class="cta-wrapper"><a href="https://app.sitesearch360.com/signup" class="cta" target="_blank" @click="onSignup">{{texts.installation.signUp}}</a></div>
        </section>
        <section aria-label="Step 2">
            <h3>{{texts.installation.step}} {{stepOffset+1}}: {{texts.installation.detailsPrompt}}</h3>
            <div class="row stretch" style="margin: 0.5em 0;">
                <label for="site-id--i">{{texts.installation.siteId}}</label>
                <input type="text" id="site-id--i" v-model="outputSiteId" style="width: 200px;" placeholder="spoonacular.com"/>
            </div>
			<p>{{texts.installation.siteIdDetailsPromptHint}}</p>
            <div v-if="hasSearchBox" class="row stretch" style="margin: 0.5em 0;">
                <label for="search-box--i">{{texts.installation.searchBoxSelector}}</label>
                <input type="text" id="search-box--i" v-model="searchBoxSelector" style="width: 200px;" placeholder="#searchBox"/>
            </div>
            <div v-if="hasSearchButton" class="row stretch" style="margin: 0.5em 0;">
                <label for="search-box--i">{{texts.installation.searchButtonSelector}}</label>
                <input type="text" id="search-box--i" v-model="searchButtonSelector" style="width: 200px;" placeholder="#searchButton"/>
            </div>
			<span v-if="hasSearchBox || hasSearchBox">
				{{texts.installation.selectorHelper}}
			</span>
            <div v-if="isEmbed" class="row stretch" style="margin: 0.5em 0;">
                <label for="search-content--i">{{texts.installation.searchResultContainerSelector}}</label>
                <input type="text" id="search-content--i" v-model="contentBlock" style="width: 200px;" placeholder="#ss360-target"/>
            </div>
			<p v-if="isEmbed">{{texts.installation.embedHint}} <code>&lt;div id="ss360-target"&gt;&lt;/div&gt;</code>.</p>
            <div v-if="isFullscreen" class="row stretch" style="margin: 0.5em 0;">
                <label for="search-trigger--i">{{texts.installation.searchTriggerSelector}}</label>
                <input type="text" id="search-trigger--i" v-model="searchTrigger" style="width: 200px;" placeholder="#ss360-trigger"/>
            </div>
			<p v-if="isFullscreen">{{texts.installation.fullscreenHint}} <code>id="ss360-trigger"</code>.</p>
        </section>
        <section aria-label="Step 3" v-if="source !== 'wordpress'">
            <h3>{{texts.installation.step}} {{stepOffset+2}}: {{texts.installation.integrationInstructions}}</h3>
            <CodeBlock data-tracking="ss360Config-new" :key="'a_'+changes">&lt;!-- Start of Site Search 360 script (put right before the closing &lt;/body&gt; tag) --&gt;<br>&lt;script type="text/javascript"&gt;<br>window.ss360Config = {{getStringifiedOutput()}};<br><br>var e=document.createElement("script");<br>e.async=!0;<br>e.src="https://cdn.sitesearch360.com/v13/sitesearch360-v13.min.js";<br>document.getElementsByTagName("body")[0].appendChild(e);<br/>&lt;/script&gt;<br>&lt;!-- End of Site Search 360 script --&gt;</CodeBlock>
            <InstallationFAQ :has-search-box="hasSearchBox" :is-fullscreen="isFullscreen" :has-search-button="hasSearchButton" :search-box-selector="searchBoxSelector" :search-trigger-selector="searchTrigger" :search-button-selector="searchButtonSelector"
				:search-box-placeholder="config.searchBox !== undefined ? config.searchBox.placeholder : undefined" />     
        </section>
        <section aria-label="Step 3" v-else>
            <h3>{{texts.installation.step}} {{stepOffset+2}}: {{texts.installation.integrationInstructionsWp}}</h3>
            <p>{{texts.installation.integrationInstructionsWp2}}</p>
            <CodeBlock data-tracking="ss360Config-new" :key="'a_'+changes">{{getStringifiedOutput()}}</CodeBlock>
            <InstallationFAQ :source="source" :has-search-box="hasSearchBox" :is-fullscreen="isFullscreen" :has-search-button="hasSearchButton" :search-box-selector="searchBoxSelector" :search-trigger-selector="searchTrigger" :search-button-selector="searchButtonSelector"
				:search-box-placeholder="config.searchBox !== undefined ? config.searchBox.placeholder : undefined"/>
		</section>
        <section aria-label="Step 4">
            <h3>{{texts.installation.step}} {{stepOffset+3}}: {{texts.installation.enjoy}} {{isCustomer? texts.installation.updatedStyles :texts.installation.newSiteSearch}}</h3>
        </section>
    </section>
</template>

<script>
import CodeBlock from "./CodeBlock";
import InstallationFAQ from "./InstallationFAQ";
import ConfigHelper from "../utils/ConfigHelper.js";

export default {
  name: "InstallationInstructions",
  components: { CodeBlock, InstallationFAQ },
  props: {
    config: {
      type: Object,
      required: true
    },
    siteId: {
      type: String,
      default: ""
    },
    onSignup: Function,
    source: String
  },
  computed: {
    configOutputString() {
      return JSON.stringify(this.output, null, 4);
    },
    hasSearchBox(){
        return this.config.results.fullScreenConfig===undefined;
    },
    isEmbed(){
      return this.config.results.fullScreenConfig===undefined && this.config.results.embedConfig!==undefined
    },
    isFullscreen(){
      return this.config.results.fullScreenConfig!==undefined;
    },
    hasSearchButton(){
      return this.hasSearchBox && this.config.searchBox.searchButton!==undefined;
    },
    isCustomer(){
      return this.siteId !== undefined && this.siteId.length > 0;
    },
    stepOffset(){
      return this.isCustomer ? 0 : 1;
    }
  },
  data() {
    return {
      searchBoxSelector: "",
      contentBlock: "",
      searchTrigger: "",
      searchButtonSelector: "",
      outputSiteId: this.siteId,
      output: this.createOutput(),
      changes: 0
    };
  },
  methods: {
    getStringifiedOutput(){
      if(this.source !== "wordpress") {
        return JSON.stringify(this.output, null, 4).replace(/\"([^(\")"]+)\":/g,"$1:");
      }
      return JSON.stringify(this.output, null, 4);
    },
    removeDefaults(configObj){
		const defaults = ConfigHelper.getExtendedDefaults(configObj.language);
       return ConfigHelper.removeDefaults(configObj, defaults)
    },
    createOutput() {
      let output = JSON.parse(JSON.stringify(this.config));
      if (this.outputSiteId !== undefined && this.outputSiteId.length > 0) {
        output.siteId = this.outputSiteId;
      }
      if (output.results.embedConfig!==undefined && this.contentBlock !== undefined && this.contentBlock.length > 0) {
        output.results.embedConfig.contentBlock = this.contentBlock;
      }
      if (
        this.searchBoxSelector !== undefined &&
        this.searchBoxSelector.length > 0
      ) {
        output.searchBox.selector = this.searchBoxSelector;
      } else if (output.results.fullScreenConfig === undefined) {
        output.searchBox.selector = "#searchBox";
      }
      if (output.results.fullScreenConfig!==undefined && this.searchTrigger !== undefined && this.searchTrigger.length > 0) {
        output.results.fullScreenConfig.trigger = this.searchTrigger;
	  }
	  const moreResultsButton = output.results.moreResultsButton;
      output = this.removeDefaults(output);
      if(output.results===undefined){
        output.results = {};
	  }
      if (moreResultsButton===undefined){
        output.results.moreResultsButton = null;
      }
      if(JSON.stringify(output.results)=="{}"){
        delete output['results'];
	  }
	  if (window.location.search.indexOf('ecom=1') !== -1) {
		  output.ecom = true;
	  }
      output.showErrors = false;
      return output;
    },
    updateOutput() {
      this.output = this.createOutput();
      this.changes++;
    },
    updateLocalStorage(key, value){
      if(!('localStorage' in window)){
        return;
      }
      if(key && key.length > 0){
        window.localStorage.setItem(key, value);
      }else{
        window.localStorage.removeItem(key);
      }
    },
    getFromLocalStorage(key){
      if(!('localStorage' in window)){
        return "";
      }
      return window.localStorage.getItem(key) || "";
    }
  },
  watch: {
    outputSiteId() {
      this.updateOutput();
      this.updateLocalStorage("siteId", this.outputSiteId);
      this.config.siteId = this.outputSiteId;
    },
    contentBlock() {
      this.updateOutput();
      this.updateLocalStorage("contentBlock", this.contentBlock);
      if(this.config.results === undefined){
        this.config.results = {};
      }
      if(this.config.results.embedConfig === undefined){
        this.config.results.embedConfig = {};
      }
      this.config.results.embedConfig.contentBlock = this.contentBlock;
    },
    searchBoxSelector() {
      this.updateOutput();
      this.updateLocalStorage("searchBoxSelector", this.searchBoxSelector);
      if(this.config.searchBox === undefined){
        this.config.searchBox = {};
      }
      this.config.searchBox.selector = this.searchBoxSelector;
    },
    searchTrigger() {
      this.updateOutput();
      this.updateLocalStorage("searchTrigger", this.searchTrigger);
      if(this.config.results === undefined){
        this.config.results = {};
      }
      if(this.config.results.fullScreenConfig === undefined){
        this.config.results.fullScreenConfig = {};
      }
      this.config.results.fullScreenConfig.trigger = this.searchTrigger;
    },
    searchButtonSelector(){
      this.updateOutput();
      this.updateLocalStorage("searchButtonSelector", this.searchButtonSelector);
      if(this.config.searchBox === undefined){
        this.config.searchBox = {};
      }
      this.config.searchBox.searchButton = this.searchButtonSelector;
    },
    config: {
        deep: true,
        handler(){
            this.updateOutput();
        }
    },
    siteId(value){
      this.outputSiteId = value;
    }
  },
  mounted(){
    const siteId = this.config.siteId || this.getFromLocalStorage("siteId");
    if(siteId){
      this.outputSiteId = siteId;
    }
    this.contentBlock = (this.config.results && this.config.results.embedConfig && this.config.results.embedConfig.contentBlock) || this.getFromLocalStorage("contentBlock");
    this.searchBoxSelector = (this.config.searchBox && this.config.searchBox.selector) || this.getFromLocalStorage("searchBoxSelector");
    this.searchTrigger = (this.config.results && this.config.results.fullScreenConfig && this.config.results.fullScreenConfig.trigger) || this.getFromLocalStorage("searchTrigger");
    this.searchButtonSelector = (this.config.searchBox && this.config.searchBox.searchButton) || this.getFromLocalStorage("searchButtonSelector");
  }
};
</script>

<style>
#app div.cta-wrapper {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding: 0.25em 0 0.75em 0;
}

#installation .row {
  max-width: 685px;
}

#installation label {
  font-weight: 500;
}
</style>


// WEBPACK FOOTER //
// src/components/InstallationInstructions.vue